import { useState, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DataTable from "cxfcomponents/Tables/DataTable";
import Checkbox from "@mui/material/Checkbox";
import StatusBadge from "cxfcomponents/StatusBadge";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import ProductCellfromUser from "layouts/pages/users/reports/components/Reviews/ProductCell";
import SoftBox from "components/SoftBox";
import { useAuth0 } from "@auth0/auth0-react";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { useRoles } from "hooks/useRoles";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { insertSharedSchedules } from "api/FlowDesignerApis";

const ShareScheduleDialog = ({
	open,
	onClose,
	schedule
}) => {
	const { user } = useAuth0();
	const [usersToShareWith, setUsersToShareWith] = useState([]);
	const [selectAllChecked, setSelectAllChecked] = useState(false);
	const { users } = useSelector((store) => store?.portal?.users);
	const { sharedSchedules } = useSelector(
		(store) => store?.designer?.calendar
	);
	const { roles } = useRoles(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleClose = () => {
		onClose();
		setSelectAllChecked(false);
		setUsersToShareWith([]);
	};

	const handleSelectedUser = (userData, isChecked) => {
		const { id, tenant_id } = userData;

		if (isChecked) {
			setUsersToShareWith((prev) => [
				...prev,
				{
					schedule_id: schedule.id,
					shared_user_id: id,
					access_type: "readOnly",
					tenant_id: schedule?.tenant_id,
					owner_id: schedule?.created_by_id,
					shared_tenant_id: tenant_id
				}
			]);

			if (getUsersToShare().length === usersToShareWith.length + 1)
				setSelectAllChecked(true);

			return;
		}

		if (selectAllChecked && !isChecked) {
			setSelectAllChecked(false);
		}

		setUsersToShareWith((prev) =>
			prev.filter((user) => !(user.shared_user_id === id))
		);
	};

	const isDisabled = (userId) => {
		return !usersToShareWith.find(
			({ shared_user_id }) => shared_user_id === userId
		);
	};

	const isChecked = (userId) =>
		usersToShareWith.some(({ shared_user_id }) => shared_user_id === userId);

	const handleShareSchedule = async () => {
		try {
			setIsSubmitting(true);
			const sharedScheduleResponse = await insertSharedSchedules(
				user?.stripeId,
				usersToShareWith
			);

			handleClose();
			if (sharedScheduleResponse.isSuccessful) {
				toast.success(
					`Connector ${schedule?.name} has been shared successfully!`
				);
			} else {
				toast.warning(`Unable to share the connector. No users selected`);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleAccessTypeChange = async (selectedOption, userId) => {
		setUsersToShareWith((prev) =>
			prev.map((user) =>
				user.shared_user_id === userId
					? {
							...user,
							access_type: selectedOption
						}
					: user
			)
		);
	};

	const toggleSelectAll = () => {
		const selected = !selectAllChecked;
		setSelectAllChecked(selected);

		if (selected) {
			setUsersToShareWith(
				getUsersToShare().map((userToShare) => ({
					schedule_id: schedule.id,
					shared_user_id: userToShare.auth0_id,
					access_type: "readOnly",
					tenant_id: schedule?.tenant_id,
					owner_id: schedule?.created_by_id
				}))
			);
		} else {
			setUsersToShareWith([]);
		}
	};

	const getUsersToShare = () => {
		const filteredUsers = users.filter(
			(userToFilter) =>
				userToFilter.auth0_id !== user.cxfUserId &&
				userToFilter.auth0_id !== schedule?.created_by_id &&
				!sharedSchedules?.find(
					({ schedule_id, shared_user_id }) =>
						schedule_id === schedule?.id &&
						shared_user_id === userToFilter.auth0_id
				)
		);

		return filteredUsers;
	};

	const dataTableData = useMemo(() => {
		return {
			columns: [
				{
					Header: "share to",
					accessor: "selectUser",
					canGroupBy: false,
					disableSortBy: true,
					disableGlobalFilter: true,
					Cell: ({ value }) => {
						return (
							<Checkbox
								checked={isChecked(value.id)}
								onChange={(event) =>
									handleSelectedUser(value, event.target.checked)
								}
							/>
						);
					}
				},
				{
					Header: "users",
					accessor: "users",
					width: 500,
					disableSortBy: false,
					canGroupBy: false,
					disableGlobalFilter: true,
					defaultSortOrder: "desc"
				},
				{
					Header: "name",
					accessor: "name",
					show: false
				},
				{
					Header: "status",
					accessor: "status",
					Cell: ({ value }) => {
						return <StatusBadge status={value} />;
					},
					canGroupBy: false,
					disableGlobalFilter: true
				},
				{
					Header: "access type",
					accessor: "accessType",
					canGroupBy: false,
					disableSortBy: true,
					disableGlobalFilter: true,
					Cell: ({ value }) => {
						const { id } = value;
						return (
							<SoftBox
								width="250px"
								className="inline-select-wrapper"
							>
								<SoftSelect
									key={id}
									isDisabled={isDisabled(id)}
									options={[
										{ value: "readOnly", label: "Read Only" },
										{ value: "editor", label: "Editor" }
									]}
									defaultValue={{
										value: isDisabled(id)
											? "readOnly"
											: usersToShareWith?.find(
													(item) => item.shared_user_id === id
												)?.access_type,
										label: isDisabled(id)
											? "Select access type"
											: usersToShareWith?.find(
														(item) => item.shared_user_id === id
												  )?.access_type === "readOnly"
												? "Read Only"
												: usersToShareWith?.find(
															(item) =>
																item.shared_user_id === id
													  )?.access_type === "editor"
													? "Editor"
													: "Read Only"
									}}
									onChange={({ value: selectedOption }) => [
										handleAccessTypeChange(selectedOption, id)
									]}
								/>
							</SoftBox>
						);
					}
				}
			],
			rows: getUsersToShare().map((item) => {
				return {
					selectUser: {
						id: item.auth0_id,
						name: `${item.first_name} ${item.last_name}`,
						tenant_id: item.tenant_id
					},
					users: (
						<ProductCellfromUser
							id={item.auth0_id}
							image={item.image}
							name={`${item.first_name} ${item.last_name}`}
							firstName={item.first_name}
							lastName={item.last_name}
							email={item.email_address}
							checked={true}
						/>
					),
					name: `${item.first_name} ${item.last_name}`,
					role: (() => {
						if (roles?.length !== 0) {
							if (!item.role_id) {
								return "";
							} else {
								const roleName = roles?.find(
									(role) => role.id === item.role_id
								)?.name;
								return roleName ? roleName : "";
							}
						} else {
							return "";
						}
					})(),
					lastActive: "",
					status: item?.status,
					accessType: {
						id: item.auth0_id,
						name: `${item.first_name} ${item.last_name}`
					}
				};
			})
		};
	}, [
		roles,
		users,
		selectAllChecked,
		schedule,
		usersToShareWith,
		sharedSchedules
	]);

	return (
		<Dialog
			fullWidth
			open={open}
			onClose={handleClose}
			PaperProps={{
				style: {
					maxWidth: "1020px"
				}
			}}
		>
			<DialogContent className="share-flow-content-index-2">
				<DialogTitle sx={{ marginLeft: "8px" }}>
					Share Schedule: {schedule?.name}
				</DialogTitle>
				<SoftBox
					width={{ xs: "100%" }}
					maxWidth={{ xl: "100px" }}
					sx={{ marginLeft: "24px" }}
				>
					<SoftTypography
						variant="h6"
						mb={1}
					>
						<Checkbox
							checked={selectAllChecked}
							onChange={toggleSelectAll}
						/>
						Select All
					</SoftTypography>
				</SoftBox>
				<DataTable
					table={dataTableData}
					hiddenTopButtons={true}
					flowView="list"
					withSearchbar={true}
				/>
			</DialogContent>
			<DialogActions>
				<SoftButton
					variant="outlined"
					color="info"
					onClick={handleClose}
				>
					Close
				</SoftButton>
				<SoftButton
					color="info"
					onClick={handleShareSchedule}
					disabled={!(usersToShareWith?.length > 0)}
				>
					{isSubmitting ? (
						<CircularProgress
							size={24}
							sx={{
								color: "#fff",
								position: "absolute",
								top: "50%",
								left: "50%",
								marginTop: "-12px",
								marginLeft: "-12px"
							}}
						/>
					) : (
						"Share"
					)}
				</SoftButton>
			</DialogActions>
		</Dialog>
	);
};

export default ShareScheduleDialog;
